import {Database, ViewFilterT, useViewQuery} from '@tnc/shared';
import clsx from 'clsx';
import {debounce} from 'lodash';
import React, {ChangeEvent, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import ReactPaginate from 'react-paginate';
import {twMerge} from 'tailwind-merge';

import {dataTableViewActionT, dataTableViewColumnT, sortOptionViewT} from '@/lib/types/dataTableView.types';
import {ReactPaginateClickEventT, VALID_LIMITS, limitT} from '@/lib/types/pagination.types';

import {getSortOptionsForView} from '@/lib/constants/sortOptionsView';

import {LoadingSpinningLogoComponent} from '@/components/loadingSpinningLogoComponent';

export type DataTableViewRefT = {
  refetch: () => void;
  setLimit: (limit: limitT) => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

export type DataTableViewPropsT<
  V extends keyof Database['public']['Views'],
  RowType extends Database['public']['Views'][V]['Row'] = Database['public']['Views'][V]['Row'],
> = {
  // Data
  view: V;
  columnsDisplay: dataTableViewColumnT<V, RowType>[];
  // Options
  actionsDisplay?: dataTableViewActionT<V, RowType>[];
  limit?: limitT;
  filters?: ViewFilterT<V>[];
  sortOptions?: sortOptionViewT<V>[];
  searchColumn?: keyof Database['public']['Views'][V]['Row'];
  enabled?: boolean;
  // Display
  title?: string;
  className?: string;
  addButtonLabel?: string;
  // Display Optionals
  raw?: boolean;
  hideSearch?: boolean;
  hideRefresh?: boolean;
  hideSort?: boolean;
  hideTitle?: boolean;
  hideAdd?: boolean;
  hideShowing?: boolean;
  hideLimit?: boolean;
  hideLimitIcon?: boolean;
  hidePagination?: boolean;
  hideLoading?: boolean;
  preventScrolling?: boolean;
  noResultsMessage?: string;
  // Functions
  onRefresh?: () => void;
  onRefetch?: (refetch: () => void) => void;
  onSearch?: (value: string, debounceTime?: number) => void;
  onAdd?: () => void;
  onSort?: (option: sortOptionViewT<V>) => void;
  onSetLimit?: (limit: limitT) => void;
  onGoToPage?: (page: number) => void;
  onPrev?: () => void;
  onNext?: () => void;
};

function DataTableViewComponent<
  V extends keyof Database['public']['Views'],
  RowType extends Database['public']['Views'][V]['Row'] = Database['public']['Views'][V]['Row'],
>(props: DataTableViewPropsT<V, RowType>, ref: Ref<DataTableViewRefT>) {
  const {
    title,
    view,
    limit: _limit = 5,
    columnsDisplay: tableColumns = [],
    actionsDisplay: tableActions,
    sortOptions = getSortOptionsForView<V>(view),
    searchColumn,
    hideSearch = false,
    hideRefresh = false,
    hideSort = false,
    hideTitle = false,
    hideAdd = false,
    hideShowing = false,
    hideLimit = false,
    hideLimitIcon = false,
    hidePagination = false,
    hideLoading = false,
    raw = false,
    className,
    addButtonLabel = 'Create New',
    filters,
    preventScrolling = false,
    noResultsMessage = 'No results found',
    enabled = true,
    onRefresh,
    onSearch,
    onAdd,
    onSort,
    onSetLimit,
    onGoToPage,
    onPrev,
    onNext,
  } = props;

  const defaultSortOption = sortOptions.find(option => option.default) ?? sortOptions[0];

  // State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<limitT>(_limit);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSortOption, setSortSelectedOption] = useState<sortOptionViewT<V>>(defaultSortOption);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null!);

  // Data Hook
  const {
    data: {data = [], currentPage = 1, totalPages = 0} = {},
    isLoading,
    refetch,
  } = useViewQuery({
    view,
    page,
    limit,
    searchTerm,
    searchColumn,
    orderBy: {
      column: selectedSortOption.column,
      ascending: selectedSortOption.direction === 'asc',
    },
    filters,
    enabled,
  });

  // Reactivity
  useImperativeHandle(ref, () => ({
    refetch,
    setLimit,
    goToPage,
    nextPage: handleNext,
    prevPage: handlePrev,
  }));

  // Pagination handlers
  const handlePrev = useCallback(() => {
    setPage(prev => Math.max(prev - 1, 1));
    onPrev?.();
    if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
  }, [onPrev, preventScrolling]);

  const handleNext = useCallback(() => {
    setPage(prev => Math.min(prev + 1, totalPages));
    onNext?.();
    if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
  }, [onNext, preventScrolling, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      setPage(Math.min(Math.max(1, page), totalPages));
      onGoToPage?.(page);
      if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
    },
    [onGoToPage, preventScrolling, totalPages]
  );

  // Event handlers
  const handleSort = useCallback(
    (option: sortOptionViewT<V>) => {
      setSortSelectedOption(option);
      onSort?.(option);
      goToPage(1);
    },
    [goToPage, onSort]
  );

  const handleRefresh = useCallback(() => {
    searchInputRef.current.value = '';
    setSearchTerm('');
    refetch();
    onRefresh?.();
  }, [onRefresh, refetch]);

  const handleSetLimit = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newLimit = Number(event.target.value) as limitT;
      setLimit(newLimit);
      onSetLimit?.(newLimit);
      goToPage(1);
    },
    [goToPage, onSetLimit]
  );

  const handlePageClick = useCallback(
    (event: ReactPaginateClickEventT) => {
      const newPage = event.selected + 1;
      if (newPage !== page) {
        goToPage(newPage);
      }
    },
    [goToPage, page]
  );

  // Search handling
  const handleSearchData = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        onSearch?.(value);
        goToPage(1);
      }, 300),
    [goToPage, onSearch]
  );

  // Sorting options
  const sortSortOptions = useCallback(
    (options: sortOptionViewT<V>[]) =>
      options.map(option => ({
        ...option,
        selected: option.column === selectedSortOption.column && option.direction === selectedSortOption.direction,
      })),
    [selectedSortOption]
  );

  // Render helpers
  const defaultRender = useCallback((row: RowType, field: keyof Database['public']['Views'][V]['Row']) => {
    const value = row[field];
    return value === null || value === undefined ? '-' : String(value);
  }, []);

  const maxWidthClass = useMemo(() => {
    const totalColumns = (tableColumns?.length ?? 0) + (tableActions ? 1 : 0);
    return `max-w-[${Math.floor(100 / totalColumns)}%]`;
  }, [tableColumns?.length, tableActions]);

  // Loading state
  if (isLoading && !hideLoading) {
    return <LoadingSpinningLogoComponent />;
  }

  return (
    <div className={twMerge('grid', className)}>
      <div className="col-span-12">
        <div className={clsx('box', {'!border-0 !shadow-none': raw})}>
          {!raw && !hideTitle && (
            <div className="box-header">
              <div className="flex w-full flex-wrap items-center justify-between gap-4">
                <h5 className="box-title">{title}</h5>
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  {/* Refresh Button */}
                  {!hideRefresh && (
                    <button
                      onClick={handleRefresh}
                      className="ti-btn ti-btn-primary !m-0 !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                    >
                      <i className="ri-refresh-line"></i>
                    </button>
                  )}
                  {/* Search Input */}
                  {!hideSearch && (
                    <div className="me-3">
                      <div className="search-form h-full">
                        <input
                          className="ti-form-control form-control-sm h-full"
                          type="text"
                          placeholder="Search Here"
                          aria-label=".form-control-sm example"
                          onChange={() => handleSearchData(searchInputRef.current.value)}
                          ref={searchInputRef}
                        />
                      </div>
                    </div>
                  )}
                  {/* Sort Dropdown */}
                  {!hideSort && (
                    <div className="hs-dropdown ti-dropdown">
                      <button className="ti-btn ti-btn-primary !m-0 !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white">
                        Sort By <i className="ri-arrow-down-s-line ms-1 inline-block align-middle"></i>
                      </button>
                      <ul className="hs-dropdown-menu ti-dropdown-menu hidden" role="menu">
                        {sortSortOptions(sortOptions).map((option, index) => (
                          <li key={index}>
                            <button
                              onClick={() => handleSort(option)}
                              className="ti-dropdown-item !px-[0.9375rem] !py-2 !text-[0.8125rem] !font-medium"
                            >
                              {option.label}
                              {option.selected && <i className="ri-check-line ms-2 text-primary"></i>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Add Button */}
                  {!hideAdd && (
                    <div>
                      <button
                        onClick={onAdd}
                        className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                      >
                        {addButtonLabel}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="box-body">
            <div className="table-responsive">
              <table className="table-hover table-bordered table min-w-full whitespace-nowrap">
                <thead>
                  <tr>
                    {tableActions && <th className="text-start">Actions</th>}
                    {tableColumns?.map((column, index) => (
                      <th key={index} className="text-start">
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {/* No results :( */}
                {data.length === 0 && (
                  <tbody>
                    <tr className="border">
                      <td colSpan={tableColumns.length}>
                        <div className="text-center font-bold">{noResultsMessage}</div>
                      </td>
                    </tr>
                  </tbody>
                )}
                {/* We have results */}
                {data.length > 0 && (
                  <tbody>
                    {data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light"
                      >
                        {tableActions && tableActions.some(action => action.permission(row)) && (
                          <td>
                            {tableActions.some(action => action.render) ? (
                              // Use custom render function if provided
                              tableActions.map((action, actionIndex) =>
                                action.render ? (
                                  <React.Fragment key={actionIndex}>{action.render(row)}</React.Fragment>
                                ) : null
                              )
                            ) : (
                              // Default action buttons rendering
                              <div className="flex flex-row items-center justify-center gap-2 text-[0.9375rem]">
                                {tableActions
                                  .filter(action => action.isShown?.(row) ?? true)
                                  .map(
                                    (action, actionIndex) =>
                                      action.permission(row) && (
                                        <button
                                          key={actionIndex}
                                          onClick={() => action.handler(row)}
                                          className={`ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] !gap-0 text-[0.8rem] bg-${action.color}/10 text-${action.color} hover:bg-${action.color} hover:text-white hover:border-${action.color}`}
                                        >
                                          <i className={action.icon}></i>
                                        </button>
                                      )
                                  )}
                              </div>
                            )}
                          </td>
                        )}
                        {tableColumns?.map((column, colIndex) => (
                          <td className={`overflow-hidden ${maxWidthClass}`} key={colIndex}>
                            {column.render ? column.render(row, column.field) : defaultRender(row, column.field)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
          {!raw && !(hideShowing && hideLimitIcon && hideLimit && hidePagination) && (
            <div className="box-footer">
              <div className="flex items-center justify-between">
                {!(hideShowing && hideLimitIcon && hideLimit) && (
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-400">
                    {!hideShowing && (
                      <div>
                        Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, data?.length)} of{' '}
                        {totalPages * limit} entries
                      </div>
                    )}
                    {!hideLimitIcon && <i className="bi bi-arrow-right ms-2 font-semibold"></i>}
                    {!hideLimit && (
                      <>
                        <div>
                          <select
                            className="ti-form-control form-select-sm form-select ml-4"
                            defaultValue={limit}
                            onChange={handleSetLimit}
                          >
                            {VALID_LIMITS.map((value, index) => (
                              <option key={index} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {!hidePagination && (
                  <div className="pagination-style-4">
                    <ReactPaginate
                      forcePage={currentPage - 1}
                      onPageChange={handlePageClick}
                      pageCount={Math.max(1, totalPages)}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={2}
                      renderOnZeroPageCount={null}
                      className="ti-pagination select-none gap-1"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      activeLinkClassName="active"
                      breakLabel={'...'}
                      breakLinkClassName="page-item"
                      nextClassName={clsx({'hidden disabled': currentPage === totalPages})}
                      nextLinkClassName="page-link"
                      nextLabel={'Next'}
                      previousClassName={clsx({'hidden disabled': currentPage === 1})}
                      previousLinkClassName="page-link"
                      previousLabel={'Prev'}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DataTableView = React.forwardRef(DataTableViewComponent) as unknown as <
  V extends keyof Database['public']['Views'],
  RowType extends Database['public']['Views'][V]['Row'] = Database['public']['Views'][V]['Row'],
>(
  props: DataTableViewPropsT<V, RowType> & React.RefAttributes<DataTableViewRefT>
) => React.ReactElement | null;
